import moment from 'moment';
import { Subscription } from 'rxjs';
import { PassportScanInfoInt } from '@interfaces/*';
import { Passenger as PassengerOld } from '@app/models';
import { FlightSummary, Passenger } from '@app/models/micro';
import { Reservation } from '@app/models/micro/reservation.model';
import { AdminDataInt } from '@app/interfaces/admin.interface';
import { CARRY_ON_BASIC_FARE_FAMILIES, FARES_FAMILY, PAX_TYPE, StatusBag } from '@app/enums';
import { environment } from '@env/environment';
import { fixOcrMrzName } from './fixOcrMrzName';
import { hasCarryOnOffersAvailable } from './carryOnAvailability';
import { getClosingMinutes } from './general';

const specialCountryCodeMapping = {
  D: 'DEU',
};
function parseDate(day: number, month: number, year: number): string {
  const pDate = moment(`${day}-${month}-${year}`, 'DD-MM-YYYY');

  const sDate = pDate.toISOString(false).split('T');

  return sDate[0];
}

function abbreviateString(string: string, maxLength: number) {
  if (string == null || maxLength == null) {
    return string;
  }
  let newString = string;
  if (newString.length >= maxLength) {
    newString = newString.substring(0, maxLength - 2);
    newString += '..';
  }
  return newString;
}

/**
 * shortens a Frequent Flyer name if the name + number length is >= 32
 * @param passenger
 * @param shortenNames are there enough passengers to cause the name ot shorten
 */
function shortenFfName(passenger, shortenNames = true) {
  if (passenger.frequentFlyerProgram == null || passenger.frequentFlyerNumber == null) {
    return '';
  }
  const characters = passenger.frequentFlyerProgram.length + passenger.frequentFlyerNumber.length;
  const shortenAmount = characters - 32;
  if (shortenNames && shortenAmount > 0) {
    const displayName = passenger.frequentFlyerProgram.substring(
      0,
      passenger.frequentFlyerProgram.length - shortenAmount
    );
    return `${displayName}..`;
  } else return passenger.frequentFlyerProgram;
}

function getUniqueId(parts: number): string {
  const stringArr = [];
  for (let i = 0; i < parts; i++) {
    // tslint:disable-next-line:no-bitwise
    const S4 = (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
    stringArr.push(S4);
  }
  return stringArr.join('-');
}

const PNR_LENGTH = 6;
const TICKET_LENGTH = 13;
const PNR_REGEX = /^[A-Z]{6}$/;
const TICKET_REGEX = /^[0-9]{13}$/;

type PnrKind = 'PNR' | 'TICKET' | 'INVALID';

function sanitizePnrInput(input: string | null | undefined): string {
  if (!input) return '';
  return input.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

function getPnrType(input: string | null | undefined): PnrKind {
  const sanitized = sanitizePnrInput(input);
  if (sanitized.length === PNR_LENGTH && PNR_REGEX.test(sanitized)) return 'PNR';
  if (sanitized.length === TICKET_LENGTH && TICKET_REGEX.test(sanitized)) return 'TICKET';
  return 'INVALID';
}

function validatePNR(pnr: string | null | undefined): boolean {
  return getPnrType(pnr) !== 'INVALID';
}

function unsubscribe(subs: Subscription[]) {
  subs.map((sub) => {
    if (sub) {
      sub.unsubscribe();
    }
  });
}

function parsePassportInfo(track1, track2) {
  const passportInfo: PassportScanInfoInt = {};

  if (!track1 || !track2) {
    return passportInfo;
  } else if (track1.length < 5 || track2.length < 27) {
    return passportInfo;
  }

  let regExp = new RegExp('<', 'g');

  try {
    passportInfo.country = track1.substr(2, 3).replace(regExp, '');
    passportInfo.country = specialCountryCodeMap(passportInfo.country);

    let name = track1.substr(5);
    if (name.indexOf('<<') > 0) {
      passportInfo.lastName = fixOcrMrzName(name.split('<<')[0].replace(regExp, ' '));
      passportInfo.firstName = fixOcrMrzName(name.split('<<')[1].replace(regExp, ' '));
    }

    passportInfo.number = track2.substring(0, 9).replace(regExp, '');

    passportInfo.issuingCountry = track2.substring(10, 13).replace(regExp, '');
    passportInfo.issuingCountry = specialCountryCodeMap(passportInfo.issuingCountry);

    let birthdate = track2.substring(13, 19);
    passportInfo.birthDateMonth = birthdate.substr(2, 2);
    passportInfo.birthDateDay = birthdate.substr(4, 2);

    let now = new Date();
    let currentYear = now.getFullYear();
    let currentYearStr = String(currentYear);
    let currentYearPartialStr = currentYearStr.substr(0, 2);
    let currentYearPartial = Number(currentYearPartialStr);

    let fullBirthdateYearStr = currentYearPartialStr + birthdate.substr(0, 2);
    if (Number(fullBirthdateYearStr) > currentYear) {
      passportInfo.birthDateYear = String(currentYearPartial - 1) + birthdate.substr(0, 2);
    } else {
      passportInfo.birthDateYear = fullBirthdateYearStr;
    }

    passportInfo.dob =
      passportInfo.birthDateYear +
      '-' +
      passportInfo.birthDateMonth +
      '-' +
      passportInfo.birthDateDay;

    passportInfo.gender = track2.substring(20, 21);

    let expirationDate = track2.substring(21, 27);

    const fullExpYearStr = currentYearPartialStr + expirationDate.substr(0, 2);

    passportInfo.expirationDateYear = fullExpYearStr;

    passportInfo.expirationDateMonth = expirationDate.substr(2, 2);
    passportInfo.expirationDateDay = expirationDate.substr(4, 2);
  } catch (e) {
    // Silently ignore parsing errors and return whatever info was extracted
  }
  return passportInfo;
}

function specialCountryCodeMap(countryCode) {
  let newCode = specialCountryCodeMapping[countryCode];
  if (newCode != null) {
    return newCode;
  }
  return countryCode;
}

function validateLimitBag(passengers: Passenger[]) {
  return passengers?.find(
    (p) =>
      (p.bagStack ?? []).filter((b) => b.status === StatusBag.INCLUDE).length >= 7 &&
      p.checkinStatus === true
  );
}

function removeAccents(str: string) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function scheduleChange(flightSummaryParam: FlightSummary, location: string) {
  const { departureDateTime, isDomestic } = flightSummaryParam;
  const now = new Date();

  const checkInHours = 4;
  const documentationMinutes = getClosingMinutes(isDomestic);

  const departureDate = moment(departureDateTime);

  const dateInit = departureDate.clone().subtract(checkInHours, 'hours');
  const dateEnd = departureDate.clone().subtract(documentationMinutes, 'minutes');

  const dateInitValue = dateInit.toDate();
  const dateEndValue = dateEnd.toDate();

  let disable = false;
  if (location === 'MEX') {
    if (dateInitValue > now) {
      disable = true;
    } else if (dateEndValue <= now) {
      disable = true;
    }
  }
  return disable;
}

function isNight(compareDate: string): boolean {
  const date = moment(compareDate);
  const initDate = moment('19:00', 'HH:mm');
  const finalDate = moment('06:00', 'HH:mm').add(1, 'day');

  return date.isBetween(initDate, finalDate);
}

function formatDateYYYMMDD(inputDate: string): string {
  return moment(inputDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
}

function showBaggageButtonEnabledForMainLightOutsideWindow(
  fareFamily: string,
  passengerSelected: Passenger[] | null | undefined,
  disabledBaggageDrop: boolean
): boolean {
  if (!fareFamily || fareFamily !== FARES_FAMILY.MAIN_LIGHT) {
    return false;
  }
  if (!disabledBaggageDrop) {
    return false;
  }
  const passengers = passengerSelected ?? [];
  const hasPassengersWithoutCarryOn = passengers.some((p) => {
    if (p.paxType === PAX_TYPE.INFANT) {
      return false;
    }
    const stack = p.bagStackCarryon ?? [];
    const hasCarryOnPurchased = stack.some(
      (b) => b.status === StatusBag.PAID || b.status === StatusBag.INCLUDE
    );
    return !hasCarryOnPurchased;
  });
  return hasPassengersWithoutCarryOn;
}

function showBaggageButtonEnabledForBasicOutsideWindow(
  fareFamily: string,
  passengerSelected: Passenger[] | null | undefined,
  disabledBaggageDrop: boolean,
  reservation?: Reservation,
  admin?: AdminDataInt
): boolean {
  if (!fareFamily || !CARRY_ON_BASIC_FARE_FAMILIES.includes(fareFamily as FARES_FAMILY)) {
    return false;
  }
  if (!disabledBaggageDrop) {
    return false;
  }
  //Bloquear el acceso si no existe oferta de carry-on para la ruta
  if (reservation && admin && !hasCarryOnOffersAvailable(reservation, admin)) {
    return false;
  }
  const passengers = passengerSelected ?? [];
  const hasPassengersWithout5kgAddOn = passengers.some((p) => {
    if (p.paxType === PAX_TYPE.INFANT) {
      return false;
    }
    const has5kgAddOn =
      p.bagStackCarryon?.some(
        (bag) =>
          bag.rficSubcode === '07X' &&
          (bag.status === StatusBag.PAID ||
            bag.status === StatusBag.INCLUDE ||
            bag.status === StatusBag.REDEEMED_APPLIED)
      ) ?? false;
    return !has5kgAddOn;
  });
  return hasPassengersWithout5kgAddOn;
}

function showBaggageButtonEnabledOutsideDocWindow(
  fareFamily: string,
  passengerSelected: Passenger[] | null | undefined,
  disabledBaggageDrop: boolean,
  reservation?: Reservation,
  admin?: AdminDataInt
): boolean {
  return (
    showBaggageButtonEnabledForMainLightOutsideWindow(
      fareFamily,
      passengerSelected,
      disabledBaggageDrop
    ) ||
    showBaggageButtonEnabledForBasicOutsideWindow(
      fareFamily,
      passengerSelected,
      disabledBaggageDrop,
      reservation,
      admin
    )
  );
}

function allPassengersHaveCarryOnMainLight(
  passengerSelected: Passenger[] | null | undefined
): boolean {
  const passengers = passengerSelected ?? [];
  if (passengers.length === 0) {
    return false;
  }
  const allHaveIt = passengers.every((p) => {
    if (p.paxType === PAX_TYPE.INFANT) {
      return true;
    }
    const stack = p.bagStackCarryon ?? [];
    return stack.some(
      (b) =>
        b.status === StatusBag.PAID ||
        b.status === StatusBag.INCLUDE ||
        b.status === StatusBag.REDEEMED_APPLIED
    );
  });
  return allHaveIt;
}

export {
  formatDateYYYMMDD,
  parseDate,
  abbreviateString,
  shortenFfName,
  getUniqueId,
  validatePNR,
  sanitizePnrInput,
  getPnrType,
  parsePassportInfo,
  unsubscribe,
  validateLimitBag,
  removeAccents,
  scheduleChange,
  isNight,
  showBaggageButtonEnabledForMainLightOutsideWindow,
  showBaggageButtonEnabledForBasicOutsideWindow,
  showBaggageButtonEnabledOutsideDocWindow,
  allPassengersHaveCarryOnMainLight,
};
export type { PnrKind };
